import * as React from 'react';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';
import { IBackgroundTaskQueue, IBackgroundTask, IBackgroundTaskScope } from '../../models/backgroundTasks/IBackgroundTaskQueue';
import { ControlFacet } from '../../models/controls/ControlFacet';
import { ControlHandlerProvider } from '../decorators/withControlHandler';
import { IControlFaceted } from '../decorators/withControlHandler';
import { IItemCache } from '../../models/IItemCacheObserver';

/**
 * @internal
 */
export const backgroundTaskQueueControl = new ControlFacet<{
    scope: IBackgroundTaskScope
}, {
        queue: IBackgroundTaskQueue;
    }>('backgroundTaskQueue');

/**
 * @internal
 */
export interface IBackgroundTaskContextProviderProps {
    queue?: IBackgroundTaskQueue;
    scope: IBackgroundTaskScope;
    onQueueTask?: (task: IBackgroundTask) => IBackgroundTask;
}

/**
 * @internal
 */
export interface IBackgroundTaskContextProviderState {
    hasTasks?: boolean;
}

/**
 * @internal
 */
export class BackgroundTaskContextProvider extends React.PureComponent<
    IBackgroundTaskContextProviderProps,
    IBackgroundTaskContextProviderState
    > {
    private _controlHandlers: IControlFaceted;
    private _ownQueue?: BackgroundTaskBuffer;
    private _queue: IBackgroundTaskQueue;
    private _isDisposed?: boolean;

    constructor(props: IBackgroundTaskContextProviderProps) {
        super(props);

        this.state = {};

        this._controlHandlers = backgroundTaskQueueControl.pack(this._backgroundTaskQueueControlHandler);

        const {
            onQueueTask,
            queue
        } = this.props;

        // tslint:disable-next-line:no-use-before-declare
        this._queue = queue || (this._ownQueue = new BackgroundTaskBuffer({
            onQueueTask: (task: IBackgroundTask) => {
                if (!this._isDisposed) {
                    this.setState({
                        hasTasks: true
                    });
                }

                return onQueueTask ? onQueueTask(task) : task;
            }
        }));
    }

    public render(): JSX.Element {
        return (
            <ControlHandlerProvider
                controlHandlers={ this._controlHandlers }
            >
                {
                    this._ownQueue && this.state.hasTasks ? (
                        // tslint:disable-next-line:no-use-before-declare
                        <BackgroundTaskHost key='host' queueRef={ this._ownQueue.queueRef } />
                    ) : null
                }
                <React.Fragment key='children'>
                    { this.props.children }
                </React.Fragment>
            </ControlHandlerProvider>
        );
    }

    public componentWillUnmount(): void {
        this._isDisposed = true;
    }

    private _backgroundTaskQueueControlHandler = (
        itemCache: IItemCache,
        props: typeof backgroundTaskQueueControl.inputType
    ): typeof backgroundTaskQueueControl.returnType => {
        if (props.scope !== this.props.scope) {
            return {};
        }

        if (props.queue) {
            return {};
        }

        return {
            queue: this._queue
        };
    }
}

const backgroundTaskKeyFacet = new AutoKeyFacet('backgroundTask');

/**
 * @public
 */
export interface IBackgroundTaskHostProps {
    /**
     * A ref for the internal queue, once the host is mounted.
     */
    queueRef: (queue: IBackgroundTaskQueue | null) => void;
}

/**
 * @public
 */
export interface IBackgroundTaskHostState {
    tasks: {
        [key: string]: JSX.Element;
    };
}

/**
 * Component which renders a set of components comprising active background tasks.
 * These tasks are provided along with promises which represent their lifetimes.
 * Once their promises are resolved or rejected, the components are de-rendered.
 * @internal
 */
export class BackgroundTaskHost extends React.PureComponent<IBackgroundTaskHostProps, IBackgroundTaskHostState> {
    private _backgroundTaskQueue: IBackgroundTaskQueue;
    private _isDisposed?: boolean;

    constructor(props: IBackgroundTaskHostProps) {
        super(props);

        this._backgroundTaskQueue = {
            enqueue: (task: IBackgroundTask) => this._enqueue(task)
        };

        this.state = {
            tasks: {}
        };
    }

    public componentDidMount(): void {
        this.props.queueRef(this._backgroundTaskQueue);
    }

    public render(): JSX.Element | null {
        const taskKeys = Object.keys(this.state.tasks);

        return taskKeys.length > 0 ? (
            <>
                {
                    taskKeys.map((key: string) => {
                        return (
                            <React.Fragment key={ key }>
                                { this.state.tasks[key] }
                            </React.Fragment>
                        );
                    })
                }
            </>
        ) : null;
    }

    public componentWillUnmount(): void {
        this._isDisposed = true;

        this.props.queueRef(null);
    }

    private async _enqueue(task: IBackgroundTask): Promise<void> {
        const {
            content,
            promise
        } = task;

        const key = backgroundTaskKeyFacet.serializeNext();

        this.setState((previousState: IBackgroundTaskHostState): IBackgroundTaskHostState => {
            return {
                tasks: {
                    ...previousState.tasks,
                    [key]: content
                }
            };
        });

        try {
            await promise;
        } catch (e) {
            // Ignore errors, since the original caller will also be handling the same promise.
        } finally {
            if (!this._isDisposed) {
                this.setState((previousState: IBackgroundTaskHostState): IBackgroundTaskHostState => {
                    const {
                        tasks: {
                            [key]: removedContent,
                            ...tasks
                        }
                    } = previousState;

                    return {
                        tasks: tasks
                    };
                });
            }
        }
    }
}

/**
 * @internal
 */
export interface IBackgroundTaskBufferProps {
    onQueueTask?: (task: IBackgroundTask) => IBackgroundTask;
}

/**
 * Utility which buffers a series of queued background tasks until they can be handed to a proper queue.
 * @internal
 */
export class BackgroundTaskBuffer implements IBackgroundTaskQueue {
    private _earlyTasks: IBackgroundTask[];
    private _queue: IBackgroundTaskQueue | null;
    private _onQueueTask?: (task: IBackgroundTask) => IBackgroundTask;

    constructor(props: IBackgroundTaskBufferProps = {}) {
        this._earlyTasks = [];

        this._queue = null;

        this._onQueueTask = props && props.onQueueTask;
    }

    public enqueue = (task: IBackgroundTask): void => {
        const onQueueTask = this._onQueueTask;

        if (onQueueTask) {
            task = onQueueTask(task);
        }

        const backgroundTaskQueue = this._queue;

        if (backgroundTaskQueue) {
            backgroundTaskQueue.enqueue(task);
        } else {
            this._earlyTasks.push(task);
        }
    }

    /**
     * A function to pass to BackgroundTaskHost so when it initializes, this queue
     * can be piped into its internal queue.
     */
    public queueRef = (backgroundTaskQueue: IBackgroundTaskQueue | null): void => {
        this._queue = backgroundTaskQueue;

        if (backgroundTaskQueue) {
            const tasks = this._earlyTasks.splice(0, Infinity);

            for (const task of tasks) {
                backgroundTaskQueue.enqueue(task);
            }
        }
    }
}
